import { appTiers, formatAppPrice, type PublicApp, type PublicAppTierOption } from '@/content/apps';

/**
 * Texto de cada recurso de nível. O catálogo da API só manda a chave; a frase é
 * da landing. Chave que não está aqui não aparece na comparação: melhor omitir
 * do que mostrar «dashboards_full» ao visitante.
 */
const FEATURE_LABEL: Record<string, string> = {
  dashboards_basic: 'Painéis básicos de NPS, CSAT e CES',
  dashboards_full: 'Painéis completos: filtros por serviço, profissional, unidade e sentimento, comparativos e exportação',
  cases_sla: 'Caso de recuperação com prazo para a equipe tratar a nota ruim',
  manual_send: 'Envio avulso da pesquisa, para um cliente ou uma lista',
  ai_assist: 'IA assistiva na leitura dos comentários',
  auto_actions: 'Ação automática por faixa de nota, como abrir caso ou mandar uma mensagem',
  whatsapp_flow: 'Pesquisa em formulário dentro do WhatsApp, com quem abriu e quem parou no meio',
};

export interface TierComparisonRow {
  key: string;
  label: string;
  /** Uma entrada por nível, na ordem de `tiers`: `true`/`false` ou um texto curto. */
  values: Array<boolean | string>;
}

export interface TierComparisonData {
  tiers: PublicAppTierOption[];
  rows: TierComparisonRow[];
}

function usageText(tier: PublicAppTierOption, unit: string | null): string | null {
  if (!tier.included_usage || tier.included_usage <= 0) return null;
  const plural = unit ? ` ${unit}${tier.included_usage === 1 ? '' : 's'}` : '';
  return `${tier.included_usage.toLocaleString('pt-BR')}${plural}`;
}

/** Linhas da comparação entre níveis. `null` quando o app não tem níveis. */
export function tierComparison(app: PublicApp): TierComparisonData | null {
  const tiers = appTiers(app);
  if (tiers.length === 0) return null;
  const unit = app.pricing?.usage_unit ?? null;

  // A ordem das linhas é a ordem em que o recurso aparece pela primeira vez, do nível mais simples ao mais completo.
  const featureKeys: string[] = [];
  for (const tier of tiers) {
    for (const key of tier.features) {
      if (FEATURE_LABEL[key] && !featureKeys.includes(key)) featureKeys.push(key);
    }
  }
  const rows: TierComparisonRow[] = featureKeys.map((key) => ({
    key,
    label: FEATURE_LABEL[key],
    values: tiers.map((tier) => tier.features.includes(key)),
  }));

  if (tiers.some((t) => usageText(t, unit))) {
    rows.push({
      key: 'included_usage',
      label: 'Franquia por mês',
      values: tiers.map((t) => usageText(t, unit) ?? 'Sem franquia'),
    });
  }
  if (tiers.some((t) => (t.overage_unit_price_cents ?? 0) > 0)) {
    rows.push({
      key: 'overage',
      label: 'Acima da franquia',
      values: tiers.map((t) => ((t.overage_unit_price_cents ?? 0) > 0 ? `${formatAppPrice(t.overage_unit_price_cents ?? 0)}${unit ? ` por ${unit}` : ' cada'}` : 'Sem cobrança extra')),
    });
  }
  return { tiers, rows };
}

/** «a partir de R$ 29,90/mês» → «R$ 29,90/mês» do nível, para o cartão de escolha. */
export function tierPriceLabel(tier: PublicAppTierOption): string {
  return `${formatAppPrice(tier.monthly_price_cents)}/mês`;
}
